// ---------------------------------------------------------------------------
// V21 — the two judgements the timesheet screen makes for itself.
//
// In a plain module rather than beside the component, because the test build compiles no
// JSX and a rule nothing can test is a rule nobody is holding to. Both of these are
// places where a display defect costs money or trust rather than looking untidy, which is
// what earns them a file.
// ---------------------------------------------------------------------------

import type { Tone } from "../design/tone";
import { english, type Translate } from "../i18n/translate";
import { minutesLong } from "./format";

export type TimesheetStep = { id: string; label: string; rank: number; available: boolean };

/**
 * Minutes as hours and minutes, and NEVER as a decimal.
 *
 * "7.5 h" and "7 h 30" are the same number and not the same sentence: the first invites
 * somebody to check it against a rate in their head and get 7 hours 50 minutes. Pay
 * disputes start exactly here.
 *
 * THE ARGUMENT IS A STRING because Postgres returns `numeric` as one — the driver will
 * not silently narrow it to a float, and `13-…` §5 forbids float arithmetic anywhere in
 * the money path. A value that is not a number is a dash rather than `NaN h`.
 */
export function minutesAsSpan(minutes: string | null): string {
  if (minutes === null) return "—";
  const n = Number(minutes);
  if (Number.isNaN(n)) return "—";
  // The owner's form (tour T-6x): "1h 30m (90 min)", in the reader's language; a negative span keeps its sign.
  return `${n < 0 ? "-" : ""}${minutesLong(Math.round(Math.abs(n)))}`;
}

/**
 * THE STATE OF ONE ROW, AS A TONE AND WORDS TOGETHER.
 *
 * `11-console-design-system.md`: teal is live and confirmed, amber is attention, red is
 * failed, and status is never conveyed by colour alone. A missing timesheet is NOT red —
 * nothing has failed, somebody has simply not done it yet — and a variance is not red
 * either, because a shift that ran long is not a fault. Reserving red for a real failure
 * is what keeps red worth reacting to at six in the morning.
 *
 * THE STEP'S LABEL COMES FROM THE SERVER'S OWN ROW. D9 shortened the chain from three
 * steps to two by decision; a label typed here would need a deploy to follow the next one.
 */
export function timesheetRowState(
  row: { timesheet_id: string | null; is_approved: boolean; next_step: string | null },
  steps: readonly TimesheetStep[],
  t: Translate = english,
): { tone: Tone; label: string } {
  if (row.timesheet_id === null) return { tone: "neutral", label: t("No hours submitted") };
  if (row.is_approved) return { tone: "live", label: t("Approved") };
  const next = steps.find((s) => s.id === row.next_step);
  return {
    tone: "attention",
    label: next ? t("Awaiting {step}", { step: next.label.toLowerCase() }) : t("Awaiting approval"),
  };
}
